import { useCallback, useState } from "react";

export const usePagination = () => {
  const [pageValues, setPageValues] = useState({
    ...pageValuesDefault,
  });

  // All updaters are functional and memoized: callers may invoke several of
  // them in the same tick (e.g. reset page + change total), and effects can
  // safely list them as dependencies without re-running every render.
  const itemsPerPageChange = useCallback((num: number) => {
    setPageValues((prev) => ({
      ...prev,
      currentPage: 1,
      itemsPerPage: num,
    }));
  }, []);

  const totalItemsChange = useCallback((num: number) => {
    setPageValues((prev) =>
      prev.totalItems === (num ?? 0) ? prev : { ...prev, totalItems: num ?? 0 },
    );
  }, []);

  const goNext = useCallback(() => {
    setPageValues((prev) => ({
      ...prev,
      currentPage: prev.currentPage + 1,
    }));
  }, []);

  const goPrevious = useCallback(() => {
    setPageValues((prev) => ({
      ...prev,
      currentPage: Math.max(1, prev.currentPage - 1),
    }));
  }, []);

  const setCurrentPage = useCallback((pageNo: number) => {
    setPageValues((prev) =>
      prev.currentPage === pageNo ? prev : { ...prev, currentPage: pageNo },
    );
  }, []);

  return {
    pageValues,
    itemsPerPageChange,
    totalItemsChange,
    goNext,
    goPrevious,
    setCurrentPage,
  };
};

interface PageValuesState {
  itemsPerPage: number;
  currentPage: number;
  totalItems: number;
}

const pageValuesDefault: PageValuesState = {
  itemsPerPage: 50,
  currentPage: 1,
  totalItems: 0,
};
